import React, { useMemo } from 'react'
import { TriangleAlert } from 'lucide-react'
import { moneyOrBlank, monthLabel } from './overview'

// ─────────────────────────────────────────────────────────────────────────────
//   Lo que todavía no está contado
//
//   `overview.not_counted` es parte de la respuesta, no una nota al pie: la
//   póliza sin fecha, la reserva calculada a un porcentaje que nadie ha
//   confirmado, la nómina con clasificación provisional. Sin ella la pantalla
//   afirma más de lo que sabe.
//
//   Por eso va JUNTO a las cifras y siempre visible, nunca en un tooltip ni
//   detrás de un desplegable. Un hueco declarado es manejable y uno silencioso
//   es un error latente, y quien mira estos números los está tomando por
//   acordados — que es precisamente lo que no son mientras esta lista tenga
//   entradas.
//
//   El backend la deriva en cada llamada y nunca la escribe: cada entrada
//   desaparece cuando se arregla la cosa que reporta, no cuando alguien se
//   acuerde de borrar una línea de código. Así que esta lista vaciándose sola
//   es el comportamiento correcto, y una lista vacía es una afirmación real
//   sobre los libros, no un estado por defecto.
// ─────────────────────────────────────────────────────────────────────────────

// Los `kind` NO se enumeran aquí. El backend tiene cinco fuentes hoy y añade
// más (`tax_rate_unconfirmed` y `payroll_kind_provisional` llegaron después de
// las otras tres), así que un `switch` por tipo significaría que una entrada
// nueva se pinta mal o no se pinta — que es el único fallo que esta lista no
// puede permitirse, porque su trabajo es justamente que no se pierda nada. Cada
// entrada trae ya su `label` y su `detail` escritos para leerse.
//
// El `scope` sí se conoce, porque cambia dónde va la entrada y no sólo qué
// dice: 'month' pertenece a la ventana que se está mirando, 'undated' no tiene
// fecha en el origen y por tanto no pertenece a ningún mes. Un lector que no
// pudiera distinguirlos creería que la póliza sin fecha es un problema de
// septiembre. Un scope que no sea ninguno de los dos se agrupa aparte en vez de
// caerse.
//
// ─────────────────────────────────────────────────────────────────────────────
//   Y desde el rango, `month` además del `scope`
//
//   Éste es el bloque que cierra el hueco que dejan los otros dos: la cascada y
//   los gastos fijos se anclan a UN mes del rango, y sin esta lista los demás
//   meses de la ventana no tendrían dónde reportar lo que les falta. Por eso el
//   agrupado baja al mes.
//
//   `month` es el mes concreto del que habla una entrada —hoy sólo
//   `recurring_unregistered`, que nombra un mes sin pago registrado— y null en
//   las que describen la ventana entera. Las dos cosas son distintas y se
//   agrupan aparte: "en julio falta PingTree" y "sobre marzo-septiembre la
//   nómina va con clasificación provisional" no son el mismo tipo de hueco, y
//   juntarlas bajo un solo encabezado haría que la segunda pareciera de julio.
//
//   Sobre un mes suelto los dos son lo mismo —el backend documenta que ahí
//   `month` es null-o-ese-mes— y el agrupado colapsa a un solo grupo, así que
//   la pantalla del mes suelto no cambia en nada.
// ─────────────────────────────────────────────────────────────────────────────

// Clave del grupo. Las entradas de ventana llevan una clave propia en vez de
// mezclarse con las de un mes: sobre un rango dicen algo del rango entero.
const WINDOW = ' window'

const groupKey = (item) => {
  const scope = item.scope || 'unknown'
  if (scope !== 'month') return scope
  return item.month || WINDOW
}

// El encabezado de un grupo. `single` es el mes suelto, donde la ventana ES un
// mes y decir "across" de un solo mes sería raro además de falso.
//
// El `hint` es opcional y sólo aparece donde el título no se explica solo. "In
// September 2026" ya dice todo lo que decía su pie, y repetirlo en gris debajo
// era una línea de prosa por grupo que no añadía nada.
const groupHeading = (key, { rangeText, single }) => {
  if (key === WINDOW) {
    return single
      ? { title: `In ${rangeText}` }
      : { title: `Across ${rangeText}`, hint: 'The range as a whole, not any one month of it.' }
  }
  if (key === 'undated') {
    return { title: 'Undated', hint: 'No date in the source, so in no month — always shown.' }
  }
  // Cualquier otra clave es un mes: el backend lo manda ya formado, así que se
  // rotula con él y no hace falta saber nada más de la entrada.
  if (/^\d{4}-\d{2}$/.test(key)) {
    return {
      title: `In ${monthLabel(key)}`,
      hint: single ? undefined : 'One month of the range.',
    }
  }
  return { title: key, hint: 'Under a scope this screen does not know about.' }
}

function NotCountedRow({ item }) {
  return (
    <li
      style={{
        display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between',
        padding: '10px 0', borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{item.label}</span>
          {/* El `kind` se pinta tal cual y sin traducir. Es el nombre que usa el
              backend, así que una entrada de un tipo que esta pantalla no
              conoce llega igualmente identificada y se puede buscar. */}
          <span className="badge badge-gray" style={{ fontSize: 10 }}>{item.kind}</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 3 }}>
          {item.detail}
        </div>
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>
        {item.amount === null || item.amount === undefined ? (
          // `amount` es null, nunca 0, donde el tamaño del hueco genuinamente
          // no se sabe — y decir "$0.00" ahí sería afirmar que no falta nada,
          // que es lo contrario de lo que la entrada significa. El `detail` de
          // la propia entrada explica por qué no hay cifra.
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Size unknown
          </span>
        ) : (
          // Ámbar: un hueco es dinero pendiente de contar, no dinero que se
          // haya movido. Es el mismo token del borde de la tarjeta, así que la
          // cifra se lee como parte de la advertencia y no como un dato más.
          <span style={{ fontWeight: 600, color: 'var(--amber)' }}>
            {moneyOrBlank(item.amount)}
          </span>
        )}
      </div>
    </li>
  )
}

export default function NotCountedPanel({ items, rangeText, single, loading }) {
  // Se agrupa conservando el orden de aparición de cada clave en vez de un
  // orden escrito aquí: el backend ya devuelve las entradas ordenadas por
  // fuente y, dentro de `recurring_unregistered`, por mes en orden. Reordenarlas
  // sería una segunda opinión sobre cuál importa más.
  const groups = useMemo(() => {
    const seen = new Map()
    for (const item of items || []) {
      const key = groupKey(item)
      if (!seen.has(key)) seen.set(key, [])
      seen.get(key).push(item)
    }
    return [...seen.entries()]
  }, [items])

  const count = (items || []).length

  return (
    <div
      className="card"
      style={{
        marginBottom: 16,
        borderLeft: '3px solid var(--amber)',
        background: 'var(--amber-bg)',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 1 }}>
          <TriangleAlert size={18} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Not counted yet</h3>
            {!loading && count > 0 && (
              <span className="badge badge-amber">{count}</span>
            )}
          </div>
          {/* Una línea. Que la lista se recalcule en cada carga se ve sola —una
              entrada desaparece cuando su causa se arregla— y decirlo costaba
              dos líneas más en el bloque que menos puede permitirse que nadie
              lo lea entero. */}
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            What the figures above do not include, or include on a basis nobody has confirmed.
          </p>
        </div>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>Loading…</div>
      ) : count === 0 ? (
        // Una lista vacía es una afirmación, no un hueco en la pantalla: dice
        // que el backend no encontró ninguna de sus cinco fuentes de duda para
        // esta ventana. Merece decirse, porque es la única forma de
        // distinguirla de un panel que no cargó.
        <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 12, lineHeight: 1.55 }}>
          Nothing outstanding — every figure on this screen is backed by a movement that was
          recorded, on a basis somebody confirmed.
        </div>
      ) : (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {groups.map(([key, rows]) => {
            const { title, hint } = groupHeading(key, { rangeText, single })
            return (
              <div key={key}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
                  {title}
                </div>
                {hint && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.5 }}>
                    {hint}
                  </div>
                )}
                <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
                  {rows.map((item, index) => (
                    // Sin `id` en el esquema y sin `kind` único: una misma
                    // fuente puede devolver varias entradas (una por persona en
                    // `tax_burden_unknown`), así que la clave lleva el label y
                    // el índice dentro del grupo.
                    <NotCountedRow key={`${item.kind}:${item.label}:${index}`} item={item} />
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
